import { type z } from 'zod';
import { SUGER_ENTITLEMENT_ID_COOKIE_NAME } from '@/components/Billing/SugerRegistration/constants';
import { HOSTNAME } from '@/helpers/url';
import { QDRANT_TECH_PREFIX, WEB_STORAGE_PREFIX } from '@/utils/constants';
import { safeSessionStorage } from '@/utils/helpers';
import {
  type UtmSearchParams,
  type BaseHubspotSearchParams,
  type rootSearchSchema,
  utmParamsSchema,
  baseHubspotParamsSchema,
  hubspotParamsSchema,
  onetrustParamsSchema,
} from '../router/utils';
import { Cookies } from './cookies';
import { storeUtmParams } from './utm';

export type UtmParamsSchema = z.infer<typeof utmParamsSchema>;
export type BaseHubspotParamsSchema = z.infer<typeof baseHubspotParamsSchema>;
export type BaseOnetrustParamsSchema = z.infer<typeof onetrustParamsSchema>;

export const UTM_PARAMS_COOKIE_NAME = 'utm_params';
export const ONETRUST_CONSENT_PARAM = 'qdrant_tech_ot_consent';
export const HUBSPOT_UTK_COOKIE = 'hubspotutk';
export const HUBSPOT_UTK_TLD_COOKIE = `${QDRANT_TECH_PREFIX}${HUBSPOT_UTK_COOKIE}`;
export const QDRANT_TECH_AJS_ANONYMOUS_ID_COOKIE_NAME = `${QDRANT_TECH_PREFIX}ajs_anonymous_id`;
export const ONETRUST_CONSENT_TLD_COOKIE = `${QDRANT_TECH_PREFIX}${ONETRUST_CONSENT_PARAM}`;
export const HUBSPOT_SIGNUP_URL_NAME = `${WEB_STORAGE_PREFIX}signupUrl`;

type AnalyticsParamsResponse = {
  utm?: z.infer<typeof utmParamsSchema>;
  hubspot?: z.infer<typeof baseHubspotParamsSchema>;
  onetrust?: z.infer<typeof onetrustParamsSchema>;
};

export function getAnalyticsParamsFromSearch(search: z.infer<typeof rootSearchSchema>): AnalyticsParamsResponse {
  if (!Object.keys(search).length) {
    return {};
  }

  const response: AnalyticsParamsResponse = {};

  [
    {
      keys: Object.keys(utmParamsSchema.shape) as (keyof UtmParamsSchema)[],
      name: 'utm',
      schema: utmParamsSchema,
    },
    // oxlint-disable-next-line sort-keys
    {
      name: 'hubspot',
      schema: hubspotParamsSchema, // with superRefine for hubspotutk formatting
      keys: Object.keys(baseHubspotParamsSchema.shape) as (keyof BaseHubspotParamsSchema)[],
    },
    {
      keys: Object.keys(onetrustParamsSchema.shape) as (keyof BaseOnetrustParamsSchema)[],
      name: 'onetrust',
      schema: onetrustParamsSchema,
    },
  ].forEach(({ name, schema, keys }) => {
    const searchParams: Record<string, string> = {};

    keys.forEach((key) => {
      if (key in search && search[key] !== undefined) {
        searchParams[key] = search[key];
      }
    });

    if (!Object.keys(searchParams).length) {
      return;
    }

    try {
      response[name as keyof AnalyticsParamsResponse] = schema.parse(searchParams);
    } catch {
      return;
    }
  });

  return response;
}

/**
 * Persists Qdrant Tech anonymous ID.
 */
export function persistMarketingSiteAnonymousId(qdrantTechAjsAnonymousId: string): void {
  Cookies.set(QDRANT_TECH_AJS_ANONYMOUS_ID_COOKIE_NAME, qdrantTechAjsAnonymousId, {
    domain: `.${HOSTNAME}`,
    expires: 365,
    sameSite: 'Lax',
    secure: true,
  });
}

/**
 * Persists Signup Url
 * In Cookies for subdomain and in sessionStorage
 */
export function persistSignupUrl(signupUrl: string): void {
  const storedUrl = safeSessionStorage.get(HUBSPOT_SIGNUP_URL_NAME);

  // The redirect that strips the UTMs lands on the same page again with no query string. It must
  // not replace the URL that still carried them.
  if (storedUrl?.includes('?') && !signupUrl.includes('?')) {
    return;
  }

  safeSessionStorage.set(HUBSPOT_SIGNUP_URL_NAME, signupUrl);
}

type BaseHubspotParamKey = keyof z.infer<typeof baseHubspotParamsSchema>;

export function persistHubspotParamsFromSearch(search: z.infer<typeof baseHubspotParamsSchema>): void {
  (Object.keys(search) as BaseHubspotParamKey[]).forEach((key) => {
    const value = search[key];
    if (!value) {
      return;
    }

    Cookies.set(key, value, {
      domain: `.${HOSTNAME}`,
      expires: 365,
      sameSite: 'Lax',
      secure: true,
    });
  });

  const hubspotutk = search.__hstc?.split('.')[1];
  if (hubspotutk) {
    Cookies.set(HUBSPOT_UTK_TLD_COOKIE, hubspotutk, {
      domain: `.${HOSTNAME}`,
      expires: 365,
      sameSite: 'Lax',
      secure: true,
    });
  }
}

/**
 * Format output needed for OneTrust consent from within the browser
 */
function createConsentString(encodedCategoryList: string) {
  return encodedCategoryList
    .split(',') // get each potential category
    .filter((cat) => cat.includes('C') && cat.length === 5) // filger to non-empty values
    .map((id) => `${id}:1`)
    .join(',');
}

/**
 * Persists Signup Url
 * In Cookies for subdomain and in sessionStorage
 */
export function persistOneTrustConsent(oneTrustConsentObj: z.infer<typeof onetrustParamsSchema>): void {
  const consentPreferences = oneTrustConsentObj[ONETRUST_CONSENT_PARAM];

  // If no preferences OR user has dismissed the OneTrust banner without making a choice
  if (!consentPreferences || consentPreferences === ',,') {
    return;
  }

  const qdrantTechOneTrustConsentStr = createConsentString(consentPreferences);
  if (!qdrantTechOneTrustConsentStr) {
    return;
  }

  Cookies.set(ONETRUST_CONSENT_TLD_COOKIE, qdrantTechOneTrustConsentStr, {
    domain: `.${HOSTNAME}`,
    expires: 365,
    sameSite: 'Lax',
    secure: true,
  });
}

export function persistUrlParamsFromSearch(search: z.infer<typeof rootSearchSchema>): {
  shouldCleanupUtmSearch: boolean;
  utmParams: UtmSearchParams | undefined;
  marketingAnonymousId: string | undefined;
  hubspotParams: BaseHubspotSearchParams | undefined;
} {
  const {
    utm: utmParams,
    hubspot: hubspotParams,
    onetrust: oneTrustParams,
  } = getAnalyticsParamsFromSearch(search) ?? {};

  // Persist Hubspot URL params
  if (hubspotParams) {
    persistHubspotParamsFromSearch(hubspotParams); // Clean up after Segment Load
  }

  // Persist UTM params
  let shouldCleanupUtmSearch = false;
  if (utmParams) {
    storeUtmParams(utmParams);
    storeUtmParams(utmParams, { firstTouch: true });
    shouldCleanupUtmSearch = true;
  }

  const { qdrant_tech_ajs_anonymous_id, sugerEntitlementId, partner } = search;

  // Persist Marketing Anonymous ID
  if (qdrant_tech_ajs_anonymous_id) {
    persistMarketingSiteAnonymousId(qdrant_tech_ajs_anonymous_id);
  }

  // OneTrust Consent from Qdrant Tech
  if (oneTrustParams) {
    persistOneTrustConsent(oneTrustParams);
  }

  // Persist sugerEntitlementId param
  if (sugerEntitlementId) {
    // Only set cookie if both sugerEntitlementId and partner are present
    if (partner) {
      Cookies.set(SUGER_ENTITLEMENT_ID_COOKIE_NAME, serializeSugerEntitlementCookie(sugerEntitlementId, partner), {
        secure: true,
      });
    }
    // Clean up URL even if only sugerEntitlementId is present
    shouldCleanupUtmSearch = true;
  }

  return {
    hubspotParams,
    marketingAnonymousId: qdrant_tech_ajs_anonymous_id,
    shouldCleanupUtmSearch,
    utmParams,
  };
}

function serializeSugerEntitlementCookie(sugerEntitlementId: string, partner?: string) {
  return JSON.stringify({ partner, sugerEntitlementId });
}
